import {
  QBox,
  QButton,
  QEmptyState,
  QSelect,
  QSpinner,
  QStack,
  QText,
  useToastProvider,
} from '@qualio/ui-components';
import { useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { changeControlApi } from '../../../../api/changeControl';
import { documentApi } from '../../../../api/document';
import { DocumentFormat } from '../../../../api/model/document';
import { useSortedList } from '../../../../hooks/SortedList';
import {
  COMMENT_HISTORY_FETCH_DATA_ERROR,
  logCustomError,
} from '../../../../messages/LogErrorMessages';
import { ChangeControl } from '../../../../types/DocumentCreate';

import { DocumentOverviewContext } from '../../DocumentOverview/Context';
import { CommentThreadList } from './CommentThreadList';
import { parseHistoricalCommentThreadsForDisplay } from './HistoricalComments/HistoricalCommentThread';
import {
  HistoricalCommentThread,
  HistoricalCommentThreadList,
} from './HistoricalComments/HistoricalCommentThreadList';

interface CommentHistoryTabProps {
  companyId: number;
  documentId: string;
  isOpen: boolean;
}

type CommmentContextSelectOptions = {
  label: CommmentContextOptions;
  value: CommentContext;
};

export enum CommmentContextOptions {
  DOCUMENT = 'Document',
  CHANGE_CONTROL = 'Change control',
}

export type CommentContext = 'document' | 'change-control';

const FROALA_CC_RETIRE_DATE = 1728691200;

interface CommentsListDescendingProps {
  commentThreads: CommentsRepository.CommentThreadFetched[] | undefined;
  selectedCommentContext: CommentContext;
  historicalCommentThreads: HistoricalCommentThread[] | undefined;
  isMultiroot: boolean;
}

const CommentsListDescending = ({
  commentThreads,
  historicalCommentThreads,
  selectedCommentContext,
  isMultiroot,
}: CommentsListDescendingProps): JSX.Element => {
  return (
    <>
      {commentThreads && commentThreads.length > 0 && (
        <CommentThreadList threads={commentThreads} isMultiroot={isMultiroot} />
      )}

      {selectedCommentContext === 'change-control' &&
        historicalCommentThreads &&
        historicalCommentThreads.length > 0 && (
          <HistoricalCommentThreadList threads={historicalCommentThreads} />
        )}
    </>
  );
};

const CommentsListAscending = ({
  commentThreads,
  historicalCommentThreads,
  selectedCommentContext,
  isMultiroot,
}: CommentsListDescendingProps): JSX.Element => {
  return (
    <>
      {selectedCommentContext === 'change-control' &&
        historicalCommentThreads &&
        historicalCommentThreads.length > 0 && (
          <HistoricalCommentThreadList threads={historicalCommentThreads} />
        )}

      {commentThreads && commentThreads.length > 0 && (
        <CommentThreadList threads={commentThreads} isMultiroot={isMultiroot} />
      )}
    </>
  );
};

export const CommentHistoryTab = ({
  companyId,
  documentId,
  isOpen,
}: CommentHistoryTabProps) => {
  const { t } = useTranslation();
  const { showToast } = useToastProvider();
  const { changeControl, qualioDocument } = useContext(DocumentOverviewContext);
  const isMultiroot =
    (qualioDocument?.editor_version?.startsWith('multiroot') &&
      qualioDocument?.document_format !== DocumentFormat.DocumentUpload) ??
    false;
  const [selectedCommentContext, setSelectedCommentContext] =
    useState<CommentContext>('document');
  const commentContextOptions: CommmentContextSelectOptions[] = [
    {
      label: t('labels.document') as CommmentContextOptions,
      value: 'document',
    },
    {
      label: t('activityHistory.changeControl') as CommmentContextOptions,
      value: 'change-control',
    },
  ];

  const onCommentHistoryContextChange = (
    selectedCommentContext: CommmentContextSelectOptions | null,
  ) => {
    if (selectedCommentContext) {
      setSelectedCommentContext(selectedCommentContext.value);
    }
  };

  const loadComments = async (companyId: number, documentId: string) => {
    return await documentApi.fetchCommentThreads(
      companyId,
      documentId,
      selectedCommentContext,
    );
  };

  const { isFetching: isFetchingComments, data: commentThreads } = useQuery({
    queryKey: ['commentHistory', companyId, documentId, selectedCommentContext],
    queryFn: () => loadComments(companyId, documentId),
    refetchOnWindowFocus: false,
    enabled: isOpen,
    onError: (error) => {
      showToast({
        'data-cy': 'comment-history-error-toast',
        id: 'error-comment-history',
        title: t('notifications.error'),
        description: t('notifications.couldNotRetrieveCommentHistory'),
        status: 'error',
      });
      logCustomError(COMMENT_HISTORY_FETCH_DATA_ERROR, { error });
    },
  });

  const loadHistoricalComments = async (
    changeControl: ChangeControl | undefined,
  ): Promise<HistoricalCommentThread[]> => {
    return await changeControlApi
      .fetchHistoricalComments(changeControl!.id)
      .then((fetchedHistoricalComments) =>
        parseHistoricalCommentThreadsForDisplay(fetchedHistoricalComments),
      );
  };

  const {
    isFetching: isFetchingHistoricalComments,
    data: historicalCommentThreads = [],
  } = useQuery({
    queryKey: ['historicalComments', companyId, changeControl],
    queryFn: () => loadHistoricalComments(changeControl),
    refetchOnWindowFocus: false,
    enabled: Boolean(
      isOpen &&
        changeControl &&
        changeControl.created_time < FROALA_CC_RETIRE_DATE &&
        changeControl.status === 'open',
    ),
    onError: (error) => {
      showToast({
        'data-cy': 'comment-history-error-toast',
        id: 'error-comment-history',
        title: t('notifications.error'),
        description: t('notifications.couldNotRetrieveCommentHistory'),
        status: 'error',
      });
      logCustomError(COMMENT_HISTORY_FETCH_DATA_ERROR, { error });
    },
  });

  const {
    sortedList: sortedHistoricalCommentThreads,
    sortDirection: commentsSortDirection,
    toggleSortDirection: toggleHistoricalCommentsSortDirection,
  } = useSortedList(historicalCommentThreads);
  const {
    sortedList: sortedCommentThreads,
    toggleSortDirection: toggleCommentThreadsSortDirection,
  } = useSortedList(commentThreads);

  const toggleBoth = () => {
    toggleHistoricalCommentsSortDirection();
    toggleCommentThreadsSortDirection();
  };

  const showSortButton = useMemo(() => {
    return (
      (historicalCommentThreads && historicalCommentThreads.length > 0) ||
      (commentThreads && commentThreads.length > 0)
    );
  }, [historicalCommentThreads, commentThreads]);

  if (isFetchingComments || isFetchingHistoricalComments) {
    return (
      <QStack height="100%" minHeight="60vh" justify="center" align="center">
        <QSpinner />
        <QText data-cy="loading-comments-history">
          {t('comments.loadingComments')}
        </QText>
      </QStack>
    );
  }

  return (
    <QStack fontSize="sm" pt="4" spacing="4">
      {!isFetchingComments && (
        <QStack direction="row" justifyContent="space-between">
          <QBox width={160} data-cy="comment-context-select">
            <QSelect
              size="sm"
              options={commentContextOptions}
              onChange={onCommentHistoryContextChange}
              value={selectedCommentContext}
              aria-label="Comment context"
            />
          </QBox>

          <>
            {showSortButton ? (
              <>
                {commentsSortDirection === 'ascending' ? (
                  <QButton
                    rightIcon="ArrowUp"
                    onClick={toggleBoth}
                    variant="ghost"
                  >
                    {t('history.oldestFirst')}
                  </QButton>
                ) : (
                  <QButton
                    rightIcon="ArrowDown"
                    onClick={toggleBoth}
                    variant="ghost"
                  >
                    {t('history.newestFirst')}
                  </QButton>
                )}
              </>
            ) : null}
          </>
        </QStack>
      )}

      <QStack fontSize="sm" spacing="4">
        {commentThreads?.length === 0 &&
        historicalCommentThreads?.length === 0 ? (
          <QEmptyState
            data-cy="comments-history-empty-placeholder"
            title={t('emptyStates.noCommentsAddedToCurrentVersion')}
            subtitle={t('emptyStates.addCommentToBeginDiscussion')}
          />
        ) : commentsSortDirection === 'descending' ? (
          <CommentsListDescending
            commentThreads={sortedCommentThreads}
            historicalCommentThreads={sortedHistoricalCommentThreads}
            selectedCommentContext={selectedCommentContext}
            isMultiroot={isMultiroot}
          />
        ) : (
          <CommentsListAscending
            commentThreads={sortedCommentThreads}
            historicalCommentThreads={sortedHistoricalCommentThreads}
            selectedCommentContext={selectedCommentContext}
            isMultiroot={isMultiroot}
          />
        )}
      </QStack>
    </QStack>
  );
};
